import { useEffect, useState } from "react";
import { IDriverDelivery } from "../../models/IDriver";
import { getDriverDeliveries } from "../../services/driver";
import { DELIVERY_STATUS_META, formatDate, formatDuration, formatTime, normalizeDeliveryStatus } from "../utils";

const PAGE_SIZE = 15;

interface Props {
  driverId: string;
  refreshKey: number;
}

function dayLabel(date: string): string {
  const d = new Date(date);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Bugün";
  if (d.toDateString() === yesterday.toDateString()) return "Dün";
  return formatDate(date);
}

function describe(item: IDriverDelivery): string {
  const status = normalizeDeliveryStatus(item.deliveryStatus);
  if (status === "Delivered" && item.deliveredAt) {
    const from = item.pickedUpAt ?? item.assignedAt;
    return `${formatTime(item.assignedAt)} atandı · ${formatTime(item.deliveredAt)} teslim · ${formatDuration(from, item.deliveredAt)}`;
  }
  if (status === "PickedUp" && item.pickedUpAt) return `${formatTime(item.pickedUpAt)} teslim alındı`;
  return `${formatTime(item.assignedAt)} atandı`;
}

export default function DeliveryHistory({ driverId, refreshKey }: Props) {
  const [items, setItems] = useState<IDriverDelivery[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    getDriverDeliveries(driverId, 0, PAGE_SIZE)
      .then((res) => {
        const list = res.data ?? [];
        setItems(list);
        setPageIndex(0);
        setHasMore(list.length === PAGE_SIZE);
      })
      .catch(() => setError("Teslimat geçmişi yüklenemedi."))
      .finally(() => setLoading(false));
  }, [driverId, refreshKey]);

  const loadMore = () => {
    const next = pageIndex + 1;
    setLoadingMore(true);
    getDriverDeliveries(driverId, next, PAGE_SIZE)
      .then((res) => {
        const list = res.data ?? [];
        setItems((prev) => [...prev, ...list]);
        setPageIndex(next);
        setHasMore(list.length === PAGE_SIZE);
      })
      .catch(() => setError("Daha fazla kayıt yüklenemedi."))
      .finally(() => setLoadingMore(false));
  };

  if (loading) {
    return (
      <div className="dp-center">
        <div className="spinner-border" style={{ color: "#ea580c" }} role="status" />
      </div>
    );
  }

  if (error && items.length === 0) {
    return <div className="dp-alert dp-alert--error"><i className="bi bi-exclamation-circle-fill"></i>{error}</div>;
  }

  if (items.length === 0) {
    return (
      <div className="dp-empty">
        <div className="dp-empty__icon is-muted"><i className="bi bi-clock-history"></i></div>
        <div className="dp-empty__title">Henüz teslimat yok</div>
        <div className="dp-empty__text">Tamamladığın teslimatlar burada listelenecek.</div>
      </div>
    );
  }

  const groups: { label: string; items: IDriverDelivery[] }[] = [];
  items.forEach((item) => {
    const label = dayLabel(item.assignedAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  });

  return (
    <div>
      {groups.map((group) => {
        const deliveredCount = group.items.filter((i) => normalizeDeliveryStatus(i.deliveryStatus) === "Delivered").length;
        return (
          <div key={group.label}>
            <div className="dp-hist-day">
              <span>{group.label}</span>
              <span style={{ textTransform: "none", letterSpacing: 0 }}>{deliveredCount} teslim</span>
            </div>
            <div className="dp-hist-list">
              {group.items.map((item) => {
                const meta = DELIVERY_STATUS_META[normalizeDeliveryStatus(item.deliveryStatus)];
                return (
                  <div key={item.id} className="dp-hist-item">
                    <div className="dp-hist-item__icon" style={{ background: meta.bg, color: meta.color }}>
                      <i className={`bi ${meta.icon}`}></i>
                    </div>
                    <div className="dp-hist-item__main">
                      <div className="dp-hist-item__no">#{item.orderNameUniqe ?? item.orderId.slice(0, 8)}</div>
                      <div className="dp-hist-item__meta">{describe(item)}</div>
                    </div>
                    <span className="dp-chip" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {error && <div className="dp-alert dp-alert--error" style={{ marginTop: 12 }}><i className="bi bi-exclamation-circle-fill"></i>{error}</div>}

      {hasMore && (
        <div className="dp-load-more">
          <button className="dp-btn dp-btn--ghost" onClick={loadMore} disabled={loadingMore} style={{ minWidth: 200 }}>
            {loadingMore ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className="bi bi-arrow-down-circle"></i> Daha fazla yükle</>}
          </button>
        </div>
      )}
    </div>
  );
}
